import { supabase } from '@/lib/supabase';

// 后台所有数据访问都走后端函数(Edge Function)，
// 由后端函数验证管理员身份后用高权限读取，前端不会直接拿到别人的敏感数据。

// ===== 独立后台登录（账号密码，密码存 Supabase 后端，前端不暴露明文） =====
const SESSION_KEY = 'jinan_admin_session';
const SESSION_TIME_KEY = 'jinan_admin_login_at';
// 后端函数会校验这个令牌（同时支持从后端 Secrets 覆盖），前后端需保持一致
const ADMIN_AUTH_TOKEN = 'jinan-admin-backend-token-2026';
// 24小时（毫秒）
const ADMIN_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export async function checkAdminCredentials(account: string, password: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke('admin-auth', {
      body: { action: 'login', account, password },
    });
    if (error) return false;
    const result = (data ?? {}) as { data?: { ok?: boolean } };
    return result.data?.ok === true;
  } catch {
    return false;
  }
}

export function isAdminLoggedIn(): boolean {
  try {
    const token = localStorage.getItem(SESSION_KEY);
    if (token !== ADMIN_AUTH_TOKEN) return false;
    const loginAt = Number(localStorage.getItem(SESSION_TIME_KEY) || '0');
    if (!loginAt) return true; // 老版本无时间戳，保持兼容
    return Date.now() - loginAt <= ADMIN_SESSION_TTL_MS;
  } catch {
    return false;
  }
}

export function setAdminSession(): void {
  try {
    localStorage.setItem(SESSION_KEY, ADMIN_AUTH_TOKEN);
    localStorage.setItem(SESSION_TIME_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function clearAdminSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_TIME_KEY);
  } catch {
    // ignore
  }
}

export interface AdminUser {
  id: string;
  phone: string;
  name: string;
  id_card: string;
  member_level: string;
  verified: boolean;
  balance: number;
  asset_value: number;
  holding_gold: number;
  order_count: number;
  role: string;
  email: string;
  created_at: string;
  max_holding_weight: number | null;
  auto_margin_enabled: boolean;
}

export interface AdminOrder {
  id: string;
  user_id: string;
  name: string;
  weight: number;
  direction: string;
  open_price: number;
  current_price: number;
  close_price: number | null;
  profit: number;
  original_margin: number;
  current_margin: number;
  craft_fee: number;
  storage_fee: number;
  storage_fee_charged: number;
  deferred_fee: number;
  total_amount: number;
  status: string;
  create_time: string;
  close_time: string | null;
  phone: string;
  user_name: string;
  lock_expire_at: string | null;
  created_at: string | null;
}

export interface Notice {
  id: number;
  tag: string;
  title: string;
  summary: string;
  date: string;
  show_on_marquee: boolean;
}

export interface DepositRecord {
  id: number;
  user_id: string;
  amount: number;
  channel: string;
  channel_id: number | null;
  channel_receiver: string | null;
  account: string | null;
  payer_name: string | null;
  bank_name: string | null;
  status: string;
  remark: string | null;
  create_time: string | null;
  created_at: string | null;
  phone: string;
  screenshots: string[];
}

export interface WithdrawRecord {
  id: number;
  user_id: string;
  asset_id: string | null;
  product: string;
  weight: number;
  method: string | null;
  amount: number;
  status: string;
  create_time: string | null;
  phone: string;
}

export interface PickupRecord {
  id: string;
  user_id: string;
  asset_ids: string[];
  product: string;
  total_weight: number;
  method: string;
  status: string;
  address_detail: string | null;
  logistics_no: string | null;
  fee: number;
  create_time: string | null;
  created_at: string | null;
  approved_time: string | null;
  approved_by: string | null;
  remark: string | null;
  phone: string;
}

export interface DashboardStats {
  total_users: number;
  admin_users: number;
  total_orders: number;
  open_orders: number;
  pending_deposits: number;
  pending_withdrawals: number;
  pending_pickups: number;
  total_balance: number;
  total_deposit_amount: number;
  holding_gold: number;
  holding_silver: number;
  holding_kgbar: number;
  total_holding_weight: number;
}

export interface UserPosition {
  id: string;
  name: string;
  weight: number;
  direction: string;
  open_price: number;
  current_price: number;
  original_margin: number;
  profit: number;
  live_profit: number;
  create_time: string;
}

export interface UserPhysicalAsset {
  id: string;
  product: string;
  weight: number;
  status: string;
  store_date: string;
  location: string;
  created_at: string;
}

export interface UserDetail {
  profile: {
    id: string;
    phone: string;
    name: string;
    id_card: string;
    id_card_front: string | null;
    id_card_back: string | null;
    email: string;
    member_level: string;
    verified: boolean;
    balance: number;
    asset_value: number;
    role: string;
    created_at: string;
    max_holding_weight: number | null;
  };
  positions: UserPosition[];
  physical_assets: UserPhysicalAsset[];
  summary: {
    total_position_weight: number;
    holding_weight: number;
    total_floating_profit: number;
    total_margin: number;
    physical_weight: number;
    net_asset_value: number;
    total_deposit_amount: number;
    total_withdraw_amount: number;
    total_profit: number;
  };
}

export interface DepositChannel {
  id: number;
  channel_type: string;
  bank_name: string | null;
  account_name: string | null;
  account_no: string | null;
  chain_type: string | null;
  remark: string | null;
  enabled: boolean;
  sort: number;
  exchange_rate: number | null;
  created_at: string | null;
}

interface FunctionResult<T> {
  data?: T;
  error?: string;
}

async function adminRequest<T>(fn: string, body: Record<string, unknown> = {}): Promise<T> {
  // 注意：这里千万不能覆盖 Authorization 头！
  // Supabase 网关会按 JWT 校验 Authorization，若塞入自定义后台令牌（非 JWT）
  // 会被网关直接拦下并返回 401 Invalid JWT，函数代码根本不会执行。
  // 正确做法：只用自定义头 x-admin-token 携带后台令牌，
  // 让 supabase-js 保持默认的合法令牌通过网关，后端函数再从 x-admin-token 校验管理员身份。
  const { data, error } = await supabase.functions.invoke(fn, {
    body,
    headers: {
      'x-admin-token': ADMIN_AUTH_TOKEN,
    },
  });
  if (error) {
    const rawErr = error as { context?: unknown; status?: number; message?: string };
    const ctx = rawErr.context;
    let serverMsg = '';
    let status: number | undefined = rawErr.status;

    if (ctx && typeof ctx === 'object' && !Array.isArray(ctx)) {
      const maybeResp = ctx as { status?: number; text?: () => Promise<string> } & Record<string, unknown>;
      if (typeof maybeResp.text === 'function' && typeof maybeResp.status === 'number') {
        // 新版 supabase-js：context 是 Response 对象，需手动读状态码和响应体
        status = maybeResp.status;
        try {
          const text = await maybeResp.text();
          try {
            const parsed = JSON.parse(text) as Record<string, unknown>;
            serverMsg = String(parsed.error || parsed.message || parsed.detail || '');
          } catch {
            serverMsg = text;
          }
        } catch {
          // ignore
        }
      } else {
        serverMsg = String(maybeResp.error || maybeResp.message || maybeResp.detail || '');
      }
    } else if (typeof ctx === 'string') {
      try {
        const parsed = JSON.parse(ctx) as Record<string, unknown>;
        serverMsg = String(parsed.error || parsed.message || parsed.detail || '');
      } catch {
        serverMsg = ctx;
      }
    }

    if (!serverMsg && status) {
      serverMsg = '请求失败（HTTP ' + status + '）';
    }
    const finalMsg = serverMsg || `调用 ${fn} 失败` + (status ? '（HTTP ' + status + '）' : '') || error.message || '请求失败';
    console.error(`[admin] ${fn} 调用失败:`, { status, serverMsg, raw: error });
    throw new Error(finalMsg);
  }
  const result = (data ?? {}) as FunctionResult<T>;
  if (result.error) throw new Error(result.error);
  return result.data as T;
}

export const adminApi = {
  changePassword: (oldPassword: string, newPassword: string) =>
    adminRequest<{ ok: boolean }>('admin-auth', { action: 'change_password', old_password: oldPassword, new_password: newPassword }),

  dashboard: () =>
    adminRequest<DashboardStats>('admin-dashboard'),

  listUsers: () =>
    adminRequest<AdminUser[]>('admin-users', { action: 'list' }),

  getUserDetail: (userId: string) =>
    adminRequest<UserDetail>('admin-users', { action: 'get_user_detail', user_id: userId }),

  setUserRole: (userId: string, role: string) =>
    adminRequest<{ ok: boolean }>('admin-users', { action: 'set_role', user_id: userId, role }),

  setHoldingLimit: (userId: string, maxHoldingWeight: number | null) =>
    adminRequest<{ ok: boolean; max_holding_weight: number | null }>('admin-users', { action: 'set_holding_limit', user_id: userId, max_holding_weight: maxHoldingWeight }),

  setAutoMargin: (userId: string, enabled: boolean) =>
    adminRequest<{ ok: boolean; auto_margin_enabled: boolean }>('admin-users', { action: 'set_auto_margin', user_id: userId, enabled }),

  deleteUser: (userId: string) =>
    adminRequest<{ ok: boolean }>('admin-users', { action: 'delete_user', user_id: userId }),

  listOrders: () =>
    adminRequest<AdminOrder[]>('admin-orders', { action: 'list' }),

  listNotices: () =>
    adminRequest<Notice[]>('admin-notices', { action: 'list' }),

  createNotice: (payload: { tag: string; title: string; summary: string; date: string; show_on_marquee?: boolean }) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'create', ...payload }),

  updateNotice: (payload: { id: number; tag: string; title: string; summary: string; date: string; show_on_marquee?: boolean }) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'update', ...payload }),

  deleteNotice: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-notices', { action: 'delete', id }),

  listDeposits: () =>
    adminRequest<DepositRecord[]>('admin-deposits', { action: 'list' }),

  confirmDeposit: (id: number, amount?: number) =>
    adminRequest<{ ok: boolean }>('admin-deposits', { action: 'confirm', id, amount }),

  rejectDeposit: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-deposits', { action: 'reject', id }),

  listWithdrawals: () =>
    adminRequest<WithdrawRecord[]>('admin-withdrawals', { action: 'list' }),

  approveWithdrawal: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-withdrawals', { action: 'approve', id }),

  rejectWithdrawal: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-withdrawals', { action: 'reject', id }),

  listPickups: () =>
    adminRequest<PickupRecord[]>('admin-pickups', { action: 'list' }),

  approvePickup: (id: string, opts?: { logistics_no?: string; fee?: number; remark?: string }) =>
    adminRequest<{ ok: boolean }>('admin-pickups', { action: 'approve', id, ...opts }),

  rejectPickup: (id: string, remark?: string) =>
    adminRequest<{ ok: boolean }>('admin-pickups', { action: 'reject', id, remark }),

  listChannels: () =>
    adminRequest<DepositChannel[]>('admin-channels', { action: 'list' }),

  createChannel: (payload: {
    channel_type: string;
    bank_name?: string;
    account_name: string;
    account_no: string;
    chain_type?: string;
    remark?: string;
    enabled: boolean;
    sort?: number;
    exchange_rate?: number | null;
  }) => adminRequest<{ ok: boolean }>('admin-channels', { action: 'create', ...payload }),

  updateChannel: (payload: Partial<DepositChannel> & { id: number }) =>
    adminRequest<{ ok: boolean }>('admin-channels', { action: 'update', ...payload }),

  deleteChannel: (id: number) =>
    adminRequest<{ ok: boolean }>('admin-channels', { action: 'delete', id }),

  getSchedule: () =>
    adminRequest<{ id: number; market_name: string; open_time: string; close_time: string; timezone: string; weekend_enabled: boolean; enabled: boolean; manual_paused: boolean; pause_reason: string | null; paused_categories: string[] | null; updated_at: string }>('admin-trading-schedule', { action: 'get' }),

  updateSchedule: (payload: {
    open_time?: string;
    close_time?: string;
    timezone?: string;
    weekend_enabled?: boolean;
    enabled?: boolean;
  }) => adminRequest<{ ok: boolean }>('admin-trading-schedule', { action: 'update', ...payload }),

  setPause: (paused: boolean, reason?: string) =>
    adminRequest<{ ok: boolean; paused: boolean }>('admin-trading-schedule', { action: 'set_pause', paused, reason }),

  toggleCategory: (category: string, paused: boolean) =>
    adminRequest<{ ok: boolean; category: string; paused: boolean; pausedCategories: string[] }>('admin-trading-schedule', { action: 'toggle_category', category, paused }),
};